import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { Plus, Pencil } from "lucide-react";

const inputCls = "mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E2240]";

export default function ModelMaster() {
  const [models, setModels] = useState([]);
  const [name, setName] = useState("");
  const [bodyType, setBodyType] = useState("");
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const load = () => api.get("/vehicle-models/master").then((r) => setModels(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    try {
      await api.post("/vehicle-models", { name, body_type: bodyType });
      toast.success(`${name} added — available for new vehicles everywhere`);
      setName(""); setBodyType(""); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const rename = async (m) => {
    try {
      await api.patch(`/vehicle-models/${m.id}`, { name: editName });
      toast.success("Renamed — reflected everywhere including history");
      setEditing(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleActive = async (m) => {
    try {
      await api.patch(`/vehicle-models/${m.id}`, { active: !m.active });
      toast.success(m.active ? `${m.name} deactivated — history stays, no new vehicles` : `${m.name} reactivated`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="mt-4 max-w-2xl">
      <p className="text-xs text-slate-500">New Kia models launch periodically — add them here, never hardcoded. Models with fleet vehicles cannot be deactivated. No hard delete: history always stays.</p>
      <div className="mt-3 flex gap-2">
        <input data-testid="model-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Model name (e.g. Sorento)" className={inputCls} />
        <input data-testid="model-bodytype-input" value={bodyType} onChange={(e) => setBodyType(e.target.value)} placeholder="Body type (optional)" className={inputCls} />
        <button data-testid="model-add-btn" onClick={add} disabled={!name.trim()}
          className="mt-1 flex items-center gap-1 rounded-xl bg-[#BB162B] px-4 text-xs font-bold text-white disabled:opacity-40"><Plus size={14} /> Add Model</button>
      </div>
      <div className="mt-3 space-y-2">
        {models.map((m) => (
          <div key={m.id} data-testid={`model-row-${m.name.replace(/ /g, "-")}`} className="rounded-xl border border-slate-200 bg-white p-3.5">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              {editing === m.id ? (
                <div className="flex gap-2 flex-1">
                  <input data-testid="model-rename-input" value={editName} onChange={(e) => setEditName(e.target.value)} autoFocus className={inputCls + " mt-0"} />
                  <button data-testid="model-rename-save" onClick={() => rename(m)} className="rounded-xl bg-[#0E2240] px-3 text-xs font-bold text-white">Save</button>
                  <button onClick={() => setEditing(null)} className="rounded-xl border border-slate-200 px-3 text-xs text-slate-500">Cancel</button>
                </div>
              ) : (
                <div>
                  <span className={`font-heading font-bold ${m.active ? "text-[#0E2240]" : "text-slate-400 line-through"}`}>{m.name}</span>
                  {m.body_type && <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[9px] font-bold uppercase text-slate-500">{m.body_type}</span>}
                  <span className={`ml-2 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase ${m.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>{m.active ? "Active" : "Inactive"}</span>
                  <div className="text-[10px] text-slate-400 mt-0.5">{m.fleet_count} fleet vehicle{m.fleet_count !== 1 && "s"} · added {(m.created_at || "").slice(0, 10)}</div>
                </div>
              )}
              {editing !== m.id && (
                <div className="flex gap-1.5">
                  <button data-testid={`model-edit-${m.name.replace(/ /g, "-")}`} onClick={() => { setEditing(m.id); setEditName(m.name); }}
                    className="rounded-lg border border-slate-200 p-2 text-[#0E2240]"><Pencil size={13} /></button>
                  <button data-testid={`model-toggle-${m.name.replace(/ /g, "-")}`} onClick={() => toggleActive(m)}
                    className={`rounded-full px-3 py-1.5 text-[10px] font-bold ${m.active ? "border border-red-200 text-[#BB162B]" : "bg-emerald-600 text-white"}`}>
                    {m.active ? "Deactivate" : "Activate"}
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
